import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import CustomerPortalLayout from '../../components/customer-portal/CustomerPortalLayout';
import TrackingModal from '../../components/customer-portal/TrackingModal';
import DocketDetailModal from '../../components/customer-portal/DocketDetailModal';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { 
  Truck, Package, Search, Calendar, Filter, 
  Eye, Download, ArrowRight, Loader2, RotateCw, ExternalLink
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

export default function CustomerDockets() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [dockets, setDockets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);

  // Filters
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'ALL');
  const [datePreset, setDatePreset] = useState('ALL');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // Modals
  const [selectedDocketId, setSelectedDocketId] = useState(null);
  const [selectedDocketNo, setSelectedDocketNo] = useState('');
  const [isTrackingOpen, setIsTrackingOpen] = useState(false);
  const [isDetailOpen, setIsDetailOpen] = useState(false);

  useEffect(() => {
    fetchDockets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, startDate, endDate]);

  const fetchDockets = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('customer_token');
      if (!token) {
        navigate('/customer-login');
        return;
      }

      const params = new URLSearchParams();
      if (statusFilter && statusFilter !== 'ALL') params.set('status', statusFilter);
      if (startDate) params.set('start_date', startDate);
      if (endDate) params.set('end_date', endDate);
      if (searchTerm) params.set('search', searchTerm);

      const res = await fetch(`${API}/api/client-portal/dockets?${params.toString()}`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });

      if (!res.ok) {
        if (res.status === 401 || res.status === 403) {
          navigate('/customer-login');
          return;
        }
        throw new Error('Failed to fetch shipments');
      }

      const data = await res.json();
      setDockets(data.items || []);
      setTotalCount(data.total || 0);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDatePresetChange = (preset) => {
    setDatePreset(preset);
    const today = new Date();
    const format = (d) => d.toISOString().split('T')[0];

    if (preset === 'ALL') {
      setStartDate('');
      setEndDate('');
    } else if (preset === 'TODAY') {
      setStartDate(format(today));
      setEndDate(format(today));
    } else if (preset === '7DAYS') {
      const past = new Date();
      past.setDate(today.getDate() - 7);
      setStartDate(format(past));
      setEndDate(format(today));
    } else if (preset === '30DAYS') {
      const past = new Date();
      past.setDate(today.getDate() - 30);
      setStartDate(format(past));
      setEndDate(format(today));
    } else if (preset === 'MONTH') {
      const first = new Date(today.getFullYear(), today.getMonth(), 1);
      setStartDate(format(first));
      setEndDate(format(today));
    }
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    fetchDockets();
  };

  const handleOpenTracking = (id, no) => {
    setSelectedDocketId(id);
    setSelectedDocketNo(no);
    setIsTrackingOpen(true);
  };

  const handleOpenDetail = (id) => {
    setSelectedDocketId(id);
    setIsDetailOpen(true);
  };

  return (
    <CustomerPortalLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold font-manrope text-slate-900">My Consignments &amp; Shipments</h1>
            <p className="text-xs text-slate-500 mt-0.5">
              Live tracking, milestone progress, and downloadable Proof of Deliveries (POD)
            </p>
          </div>
          <div className="text-right">
            <span className="text-xs font-bold text-slate-400 uppercase tracking-wider block">Total Found</span>
            <span className="text-xl font-bold text-slate-900 font-manrope">{totalCount} Dockets</span>
          </div>
        </div>

        {/* Filter Toolbar */}
        <Card className="bg-white rounded-xl p-4 border border-slate-200/80 shadow-xs space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {/* Search Input */}
            <form onSubmit={handleSearchSubmit} className="relative">
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-3.5" />
              <Input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search Docket #, Consignee..."
                className="pl-9 h-10 text-xs font-ibm rounded-xl border-slate-200"
              />
            </form>

            {/* Status Filter */}
            <Select value={statusFilter} onValueChange={(val) => setStatusFilter(val)}>
              <SelectTrigger className="h-10 text-xs font-ibm rounded-xl border-slate-200 bg-white">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All Statuses</SelectItem>
                <SelectItem value="ACTIVE">Active (In Transit)</SelectItem>
                <SelectItem value="BOOKED">Booked / Manifested</SelectItem>
                <SelectItem value="IN_TRANSIT">In Transit</SelectItem>
                <SelectItem value="OUT_FOR_DELIVERY">Out for Delivery</SelectItem>
                <SelectItem value="DELIVERED">Delivered</SelectItem>
              </SelectContent>
            </Select>

            {/* Date Preset Filter */}
            <Select value={datePreset} onValueChange={handleDatePresetChange}>
              <SelectTrigger className="h-10 text-xs font-ibm rounded-xl border-slate-200 bg-white">
                <SelectValue placeholder="Date Range" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All Time</SelectItem>
                <SelectItem value="TODAY">Today</SelectItem>
                <SelectItem value="7DAYS">Last 7 Days</SelectItem>
                <SelectItem value="MONTH">This Month</SelectItem>
                <SelectItem value="30DAYS">Last 30 Days</SelectItem>
                <SelectItem value="CUSTOM">Custom Date Range</SelectItem>
              </SelectContent>
            </Select>

            {/* Refresh / Filter Action */}
            <div className="flex items-center gap-2">
              <Button
                onClick={fetchDockets}
                variant="outline"
                className="h-10 flex-1 text-xs font-bold border-slate-200 text-slate-700 hover:bg-slate-50 rounded-xl"
              >
                <RotateCw className="w-3.5 h-3.5 mr-1.5" />
                Refresh
              </Button>
            </div>
          </div>

          {/* Custom Date Inputs if CUSTOM selected */}
          {datePreset === 'CUSTOM' && (
            <div className="flex items-center gap-3 pt-2 border-t border-slate-100 text-xs">
              <span className="text-slate-500 font-medium">From:</span>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="h-8 w-40 text-xs rounded-lg"
              />
              <span className="text-slate-500 font-medium">To:</span>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="h-8 w-40 text-xs rounded-lg"
              />
            </div>
          )}
        </Card>

        {/* Dockets Table */}
        <Card className="bg-white rounded-xl border border-slate-200/80 overflow-hidden shadow-xs">
          {loading ? (
            <div className="py-16 flex flex-col items-center justify-center text-slate-400 gap-2">
              <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
              <span className="text-xs">Loading consignment dockets...</span>
            </div>
          ) : dockets.length === 0 ? (
            <div className="py-16 flex flex-col items-center justify-center text-center p-6 text-slate-400">
              <Truck className="w-12 h-12 text-slate-300 mb-3 stroke-1" />
              <p className="text-base font-bold text-slate-800 font-manrope">No Shipments Found</p>
              <p className="text-xs text-slate-400 mt-1 max-w-sm">
                No dockets matched your current filter criteria. Try resetting filters or search query.
              </p>
              <Button
                onClick={() => { setStatusFilter('ALL'); setDatePreset('ALL'); setStartDate(''); setEndDate(''); setSearchTerm(''); }}
                className="mt-4 bg-slate-800 hover:bg-slate-900 text-white font-bold text-xs h-9 px-4 rounded-xl"
              >
                Reset All Filters
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead className="bg-slate-50 border-b border-slate-200 text-slate-500 uppercase tracking-wider font-semibold text-[11px]">
                  <tr>
                    <th className="px-5 py-3.5">Docket / AWB</th>
                    <th className="px-5 py-3.5">Date</th>
                    <th className="px-5 py-3.5">Route</th>
                    <th className="px-5 py-3.5">Consignee</th>
                    <th className="px-5 py-3.5">Cargo Specs</th>
                    <th className="px-5 py-3.5">Status</th>
                    <th className="px-5 py-3.5 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {dockets.map((d) => (
                    <tr key={d.id} className="hover:bg-slate-50/80 transition-colors">
                      <td className="px-5 py-3.5">
                        <span className="font-bold font-mono text-slate-900 block">{d.docket_number}</span>
                        {d.printed_docket_no && (
                          <span className="text-[10px] text-slate-400 font-mono block">Book: #{d.printed_docket_no}</span>
                        )}
                      </td>
                      <td className="px-5 py-3.5 text-slate-600 whitespace-nowrap">
                        {d.booking_date || '-'}
                      </td>
                      <td className="px-5 py-3.5">
                        <div className="flex items-center gap-1.5 text-slate-800 font-medium">
                          <span className="truncate max-w-[110px]">{d.origin}</span>
                          <ArrowRight className="w-3 h-3 text-slate-400 shrink-0" />
                          <span className="truncate max-w-[110px]">{d.destination}</span>
                        </div>
                      </td>
                      <td className="px-5 py-3.5">
                        <span className="font-semibold text-slate-800 truncate max-w-[150px] block">
                          {d.consignee_name}
                        </span>
                      </td>
                      <td className="px-5 py-3.5 text-slate-600">
                        <span>{d.packages} Pkgs</span> &bull; <span className="font-medium text-slate-800">{d.weight} Kg</span>
                      </td>
                      <td className="px-5 py-3.5">
                        <span className={`inline-block px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                          d.status === 'DELIVERED' || d.status === 'COMPLETED'
                            ? 'bg-emerald-100 text-emerald-800 border border-emerald-200'
                            : d.status === 'IN_TRANSIT'
                            ? 'bg-blue-100 text-blue-800 border border-blue-200'
                            : 'bg-amber-100 text-amber-800 border border-amber-200'
                        }`}>
                          {d.status?.replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td className="px-5 py-3.5 text-right space-x-1 whitespace-nowrap">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleOpenTracking(d.id, d.docket_number)}
                          className="h-8 text-xs font-bold text-blue-600 hover:text-blue-700 hover:bg-blue-50 px-2.5 rounded-lg"
                        >
                          <Truck className="w-3.5 h-3.5 mr-1" />
                          Track
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleOpenDetail(d.id)}
                          className="h-8 text-xs font-bold text-slate-600 hover:text-slate-900 hover:bg-slate-100 px-2.5 rounded-lg"
                        >
                          <Eye className="w-3.5 h-3.5 mr-1" />
                          Detail View
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>

      {/* Modals */}
      <TrackingModal
        isOpen={isTrackingOpen}
        onClose={() => setIsTrackingOpen(false)}
        docketId={selectedDocketId}
        docketNumber={selectedDocketNo}
      />
      <DocketDetailModal
        isOpen={isDetailOpen}
        onClose={() => setIsDetailOpen(false)}
        docketId={selectedDocketId}
        onTrackClick={(id, no) => handleOpenTracking(id, no)}
      />
    </CustomerPortalLayout>
  );
}
